import { Builder } from "flatbuffers";
import {
  AtBatEventDataWire,
  BattingOrderEventDataWire,
  CountEventDataWire,
  EventActionWire,
  GameEventDataWire,
  GameEventWire,
  GumboTimecodeEventDataWire,
  HandWire,
  HandedActionWire,
  HandedEventDataWire,
  InningEventDataWire,
  InningStateWire,
  LiveActionEventDataWire,
  OptionalBool,
  PlayEventDataWire,
  PlayEventWireStrikezone,
  PositionAssignmentEventDataWire,
  TeamScoreEventDataWire,
  TeamTypeWire
} from "../../../types/flatbuffers/mannequin";
import { reverseRecord } from "../../../types/util";

const stateNames = ["top", "middle", "bottom", "end"] as const;
export type InningState = typeof stateNames[number];

export function strToInningState(str: string): InningState | undefined {
  return stateNames.find(s => s === str);
}

export interface InningDetails {
  state: InningState;
  currentInning: number;
}

const handCodes = ["R", "L"] as const;
export type Hand = typeof handCodes[number];

type TeamNames = "home" | "away";
type ActionNames = "start" | "end";
type HandedActionNames = "pitch" | "bat";

export function strToHand(str?: string): Hand | undefined {
  return handCodes.find(s => s === str);
}

export interface BaseEvent {
  time: number;
  isKeyFramed?: boolean; // debug
}

// TODO: see if there's a way to automate this...
export const eventTypes = [
  "count",
  "teamScore",
  "battingOrder",
  "isLiveAction",
  "inning",
  "atBat",
  "play",
  "handed",
  "positionAssignment",
  "timecode",
] as const;

export type GameEventType = (typeof eventTypes[number]);

export interface CountEvent extends BaseEvent {
  type: "count";
  data: {
    balls: number;
    strikes: number;
    outs: number;
  }
}

export interface TeamScoreEvent extends BaseEvent {
  type: "teamScore";
  data: {
    team: TeamNames;
    runs: number;
    hits: number;
    errors: number;
  }
}

export interface BattingOrderEvent extends BaseEvent {
  type: "battingOrder";
  data: {
    team: TeamNames;
    playerIds: number[];
  }
}

/**
 * This event is based on tracking heuristics and is an approximation
 * of when there is action on the field
 */
export interface LiveActionEvent extends BaseEvent {
  type: "isLiveAction";
  data: {
    mode: boolean;
  }
}

export interface InningEvent extends BaseEvent {
  type: "inning";
  data: {
    currentInning: number;
    action: InningState;
    battingTeamId: number | undefined;
    fieldingTeamId: number | undefined;
  }
}

export interface AtBatEvent extends BaseEvent {
  type: "atBat";
  data: {
    action: ActionNames;
    index: number;
    battingOrderIndex: number;
  }
}

export interface PlayEvent extends BaseEvent {
  type: "play";
  data: {
    action: ActionNames;
    index: number;
    playId?: string;
    strikezone?: {
      top: number;
      bottom: number;
    }
  }
}

export interface HandedEvent extends BaseEvent {
  type: "handed";
  data: {
    action: HandedActionNames;
    side?: Hand;
    actorId?: number;
  };
}

export interface PositionAssignmentEvent extends BaseEvent {
  type: "positionAssignment";
  data: {
    trackUid: number;
    newPosition: number;
  };
}

export interface GumboTimecodeEvent extends BaseEvent {
  type: "timecode";
  data: {
    timecode: string;
  };
}

export type GameEvent =
  CountEvent |
  TeamScoreEvent |
  BattingOrderEvent |
  LiveActionEvent |
  InningEvent |
  AtBatEvent |
  PlayEvent |
  HandedEvent |
  PositionAssignmentEvent |
  GumboTimecodeEvent;

export type GameEventKeyFrame = { [key in GameEventType]?: GameEvent[] };

export function isGameEventKeyFrameComplete(keyFrame: GameEventKeyFrame): boolean {
  return Object.keys(keyFrame).length === eventTypes.length;
}

// serialisation logic

const toWire: {
  team: Record<TeamNames, TeamTypeWire>,
  inningState: Record<InningState, InningStateWire>,
  action: Record<ActionNames, EventActionWire>,
  handedAction: Record<HandedActionNames, HandedActionWire>,
  hand: Record<Hand, HandWire>,
} = {
  team: {
    "away": TeamTypeWire.Away,
    "home": TeamTypeWire.Home,
  },
  inningState: {
    "top": InningStateWire.Top,
    "middle": InningStateWire.Middle,
    "bottom": InningStateWire.Bottom,
    "end": InningStateWire.End,
  },
  action: {
    "start": EventActionWire.Start,
    "end": EventActionWire.End,
  },
  handedAction: {
    "pitch": HandedActionWire.Pitch,
    "bat": HandedActionWire.Bat,
  },
  hand: {
    "R": HandWire.Right,
    "L": HandWire.Left,
  }
};

const fromWire = {
  team: reverseRecord(toWire.team),
  inningState: reverseRecord(toWire.inningState),
  action: reverseRecord(toWire.action),
  handedAction: reverseRecord(toWire.handedAction),
  hand: reverseRecord(toWire.hand),
};

export class GameEventSerializer {
  static serializeBinary(gameEvent: GameEvent, builder: Builder): number | undefined {
    let dataOffset: number | undefined;
    let kind: number | undefined;
    switch (gameEvent.type) {
      case "count":
        dataOffset = CountEventDataWire.createCountEventDataWire(
          builder,
          gameEvent.data.balls,
          gameEvent.data.strikes,
          gameEvent.data.outs
        );
        kind = GameEventDataWire.CountEventDataWire;
        break;

      case "teamScore":
        dataOffset = TeamScoreEventDataWire.createTeamScoreEventDataWire(
          builder,
          toWire.team[gameEvent.data.team],
          gameEvent.data.runs,
          gameEvent.data.hits,
          gameEvent.data.errors,
        );

        kind = GameEventDataWire.TeamScoreEventDataWire;
        break;

      case "battingOrder":
      {
        const orderOffset = BattingOrderEventDataWire.createPlayerIdsVector(
          builder,
          gameEvent.data.playerIds
        );

        dataOffset = BattingOrderEventDataWire.createBattingOrderEventDataWire(
          builder,
          toWire.team[gameEvent.data.team],
          orderOffset,
        );

        kind = GameEventDataWire.BattingOrderEventDataWire;
        break;
      }

      case "isLiveAction":
        dataOffset = LiveActionEventDataWire.createLiveActionEventDataWire(
          builder,
          gameEvent.data.mode,
        );

        kind = GameEventDataWire.LiveActionEventDataWire;
        break;

      case "inning":
      {
        InningEventDataWire.startInningEventDataWire(builder);

        InningEventDataWire.addCurrentInning(builder, gameEvent.data.currentInning);
        InningEventDataWire.addAction(builder, toWire.inningState[gameEvent.data.action]);

        if (gameEvent.data.battingTeamId !== undefined) {
          InningEventDataWire.addBattingTeamId(builder, gameEvent.data.battingTeamId);
        }
        if (gameEvent.data.fieldingTeamId !== undefined) {
          InningEventDataWire.addFieldingTeamId(builder, gameEvent.data.fieldingTeamId);
        }

        dataOffset = InningEventDataWire.endInningEventDataWire(builder);

        kind = GameEventDataWire.InningEventDataWire;
        break;
      }

      case "atBat":
        dataOffset = AtBatEventDataWire.createAtBatEventDataWire(
          builder,
          toWire.action[gameEvent.data.action],
          gameEvent.data.index,
          gameEvent.data.battingOrderIndex,
        );

        kind = GameEventDataWire.AtBatEventDataWire;
        break;

      case "play":
      {
        let strikeZoneOffset: number | undefined;
        let playIdOffset: number | undefined;
        if (gameEvent.data.strikezone) {
          strikeZoneOffset =
            PlayEventWireStrikezone.createPlayEventWireStrikezone(
              builder, gameEvent.data.strikezone.top, gameEvent.data.strikezone.bottom
            );
        }
        if (gameEvent.data.playId) {
          playIdOffset = builder.createString(gameEvent.data.playId);
        }

        PlayEventDataWire.startPlayEventDataWire(builder);
        PlayEventDataWire.addAction(builder, toWire.action[gameEvent.data.action]);
        PlayEventDataWire.addIndex(builder, gameEvent.data.index);
        if (playIdOffset !== undefined) {
          PlayEventDataWire.addPlayId(builder, playIdOffset);
        }
        if (strikeZoneOffset !== undefined) {
          PlayEventDataWire.addStrikezone(builder, strikeZoneOffset);
        }
        dataOffset = PlayEventDataWire.endPlayEventDataWire(builder);

        kind = GameEventDataWire.PlayEventDataWire;
        break;
      }

      case "handed":
      {
        HandedEventDataWire.startHandedEventDataWire(builder);
        HandedEventDataWire.addAction(
          builder,
          toWire.handedAction[gameEvent.data.action]
        );
        if (gameEvent.data.side !== undefined) {
          HandedEventDataWire.addSide(
            builder,
            toWire.hand[gameEvent.data.side],
          );
        }
        if (gameEvent.data.actorId !== undefined) {
          HandedEventDataWire.addActorId(
            builder,
            gameEvent.data.actorId,
          );
        }

        dataOffset = HandedEventDataWire.endHandedEventDataWire(builder);

        kind = GameEventDataWire.HandedEventDataWire;
        break;
      }

      case "positionAssignment":
        dataOffset = PositionAssignmentEventDataWire.createPositionAssignmentEventDataWire(
          builder,
          gameEvent.data.trackUid,
          gameEvent.data.newPosition,
        );

        kind = GameEventDataWire.PositionAssignmentEventDataWire;
        break;

      case "timecode":
      {
        const timecode = builder.createString(gameEvent.data.timecode);
        dataOffset = GumboTimecodeEventDataWire.createGumboTimecodeEventDataWire(
          builder,
          timecode,
        );

        kind = GameEventDataWire.GumboTimecodeEventDataWire;
        break;
      }

      default:
        console.error("unknown game event type");
        return undefined;

    }

    if (dataOffset === undefined || kind === undefined) {
      console.error("invalid game event", gameEvent.type);
      return undefined;
    }

    GameEventWire.startGameEventWire(builder);
    GameEventWire.addDataType(builder, kind);
    GameEventWire.addData(builder, dataOffset);
    GameEventWire.addTime(builder, gameEvent.time);
    GameEventWire.addIsKeyFramed(
      builder,
      gameEvent.isKeyFramed !== undefined
        ? gameEvent.isKeyFramed ? OptionalBool.True : OptionalBool.False
        : OptionalBool.Undefined
    );
    return GameEventWire.endGameEventWire(builder);
  }

  static deserializeBinary(eventWire?: GameEventWire): GameEvent | undefined {
    if (!eventWire) return undefined;
    const kindWire = eventWire.dataType();

    const isKeyFramed = eventWire.isKeyFramed() !== OptionalBool.Undefined
      ? eventWire.isKeyFramed() === OptionalBool.True
      : undefined;

    switch (kindWire) {
      case GameEventDataWire.CountEventDataWire:
      {
        const data = eventWire.data(new CountEventDataWire()) as CountEventDataWire;
        return {
          type: "count",
          data: data.unpack(),
          time: eventWire.time(),
          isKeyFramed,
        }
      }
      case GameEventDataWire.TeamScoreEventDataWire:
      {
        const data = eventWire.data(new TeamScoreEventDataWire()) as TeamScoreEventDataWire;
        return {
          type: "teamScore",
          data: {
            team: fromWire.team[data.team()],
            runs: data.runs(),
            hits: data.hits(),
            errors: data.errors(),
          },
          time: eventWire.time(),
          isKeyFramed,
        }
      }
      case GameEventDataWire.BattingOrderEventDataWire:
      {
        const data = eventWire.data(new BattingOrderEventDataWire()) as BattingOrderEventDataWire;
        const playerIds = data.playerIdsArray();
        if (playerIds === null) {
          console.error("blank playerIds");
          return undefined;
        }
        return {
          type: "battingOrder",
          data: {
            team: fromWire.team[data.team()],
            playerIds: [...playerIds],
          },
          time: eventWire.time(),
          isKeyFramed,
        }
      }
      case GameEventDataWire.LiveActionEventDataWire:
      {
        const data = eventWire.data(new LiveActionEventDataWire()) as LiveActionEventDataWire;
        return {
          type: "isLiveAction",
          data: data.unpack(),
          time: eventWire.time(),
          isKeyFramed,
        }
      }
      case GameEventDataWire.InningEventDataWire:
      {
        const data = eventWire.data(new InningEventDataWire()) as InningEventDataWire;
        return {
          type: "inning",
          data: {
            currentInning: data.currentInning(),
            action: fromWire.inningState[data.action()],
            battingTeamId: data.battingTeamId(),
            fieldingTeamId: data.fieldingTeamId(),
          },
          time: eventWire.time(),
          isKeyFramed,
        }
      }
      case GameEventDataWire.AtBatEventDataWire:
      {
        const data = eventWire.data(new AtBatEventDataWire()) as AtBatEventDataWire;
        return {
          type: "atBat",
          data: {
            action: fromWire.action[data.action()],
            index: data.index(),
            battingOrderIndex: data.battingOrderIndex(),
          },
          time: eventWire.time(),
          isKeyFramed,
        }
      }
      case GameEventDataWire.PlayEventDataWire:
      {
        const data = eventWire.data(new PlayEventDataWire()) as PlayEventDataWire;
        return {
          type: "play",
          data: {
            action: fromWire.action[data.action()],
            index: data.index(),
            playId: data.playId() || undefined,
            strikezone: data.strikezone()?.unpack(),
          },
          time: eventWire.time(),
          isKeyFramed,
        }
      }
      case GameEventDataWire.HandedEventDataWire:
      {
        const data = eventWire.data(new HandedEventDataWire()) as HandedEventDataWire;
        return {
          type: "handed",
          data: {
            action: fromWire.handedAction[data.action()],
            side: fromWire.hand[data.side()],
            actorId: data.actorId(),
          },
          time: eventWire.time(),
          isKeyFramed,
        }
      }
      case GameEventDataWire.PositionAssignmentEventDataWire:
      {
        const data = eventWire.data(new PositionAssignmentEventDataWire()) as PositionAssignmentEventDataWire;
        return {
          type: "positionAssignment",
          data: data.unpack(),
          time: eventWire.time(),
          isKeyFramed,
        }
      }
      case GameEventDataWire.GumboTimecodeEventDataWire:
      {
        const data = eventWire.data(new GumboTimecodeEventDataWire()) as GumboTimecodeEventDataWire;
        const timecode = data.timecode();
        if (!timecode) {
          console.error("blank timecode in game event");
          return undefined;
        }
        return {
          type: "timecode",
          data: { timecode },
          time: eventWire.time(),
          isKeyFramed,
        }
      }

      case GameEventDataWire.NONE:
      default:
        console.error("invalid game event");
        return undefined;
    }
  }
}
